import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";
import Layout from "../components/app/Layout";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../contexts/I18nContext";
import api from "../lib/api";
import {
  Shield, Users, CreditCard, Flag, Search, Check, X, Trash2, Ban, RefreshCw,
  AlertTriangle, Trophy, Building2, User, Activity, DollarSign, UserCheck, MessageSquareWarning,
  TrendingUp, Sparkles, Ticket, Copy, Link as LinkIcon, Infinity as InfinityIcon, Megaphone, FlaskConical,
} from "lucide-react";
import { toast } from "sonner";
import AdminMarketplace from "../components/app/AdminMarketplace";

const TABS = [
  { id: "overview",   bg: "Преглед",     en: "Overview",    icon: Activity },
  { id: "approvals",  bg: "Одобрения",   en: "Approvals",   icon: UserCheck, badge: "pending" },
  { id: "invites",    bg: "Покани",      en: "Invites",     icon: Ticket },
  { id: "users",      bg: "Потребители", en: "Users",       icon: Users },
  { id: "bookings",   bg: "Резервации",  en: "Bookings",    icon: CreditCard },
  { id: "payments",   bg: "Плащания",    en: "Payments",    icon: DollarSign },
  { id: "reports",    bg: "Сигнали",     en: "Reports",     icon: Flag, badge: "reports_open" },
  { id: "chat",       bg: "Чат",         en: "Chat mod",    icon: MessageSquareWarning },
  { id: "analytics",  bg: "Анализи",     en: "Analytics",   icon: TrendingUp },
  { id: "marketplace", bg: "Бизнес",     en: "Business",    icon: DollarSign },
];
const VALID_BADGES = ["uefa_a", "uefa_b", "uefa_pro", "verified", "top_rated", "elite"];

export default function AdminPanel() {
  const { user, loading } = useAuth();
  const { lang } = useI18n();
  const [active, setActive] = useState("overview");
  const [stats, setStats] = useState(null);

  const reloadStats = async () => {
    try { const { data } = await api.get("/admin/stats"); setStats(data); } catch (_) { /* ignore */ }
  };
  useEffect(() => { if (user?.is_admin) reloadStats(); }, [user, active]);

  if (loading) return null;
  if (!user || !user.is_admin) return <Navigate to="/" replace />;

  const badgeCount = (key) => {
    if (!stats) return 0;
    if (key === "pending") return stats.users?.pending || 0;
    if (key === "reports_open") return stats.reports_open || 0;
    return 0;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-panel">
        <div className="flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="label-mono mb-1 flex items-center gap-1.5">
              <Shield className="w-3 h-3 text-pitch" /> // ADMIN COMMAND CENTER
            </div>
            <h1 className="font-oswald uppercase text-3xl sm:text-4xl tracking-tight">
              {lang === "bg" ? "Контролна централа" : "Mission control"}
            </h1>
            <p className="text-white/40 text-xs font-mono mt-1">
              {lang === "bg"
                ? "Модериране, одобрения, плащания, анализи — на едно място."
                : "Moderation, approvals, payments, analytics — one cockpit."}
            </p>
          </div>
        </div>

        {/* Tabs — horizontal scroll on mobile */}
        <div className="border-b border-white/8 -mx-4 md:mx-0 overflow-x-auto scroll-thin">
          <div className="flex gap-1 px-4 md:px-0 min-w-max">
            {TABS.map((tab) => {
              const isActive = active === tab.id;
              const cnt = tab.badge ? badgeCount(tab.badge) : 0;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActive(tab.id)}
                  className={`px-4 py-2.5 text-[11px] font-mono uppercase tracking-widest flex items-center gap-2 transition-colors whitespace-nowrap ${
                    isActive ? "text-pitch border-b-2 border-pitch -mb-px" : "text-white/45 hover:text-white"
                  }`}
                  data-testid={`admin-tab-${tab.id}`}
                >
                  <tab.icon className="w-3.5 h-3.5" />
                  <span>{lang === "bg" ? tab.bg : tab.en}</span>
                  {cnt > 0 && (
                    <span className={`px-1.5 py-0.5 text-[9px] font-bold rounded-full ${isActive ? "bg-pitch text-white" : "bg-red-500/85 text-white"}`}>{cnt}</span>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        <div className="animate-fade-up" style={{ opacity: 0 }}>
          {active === "overview"  && <StatsPanel lang={lang} stats={stats} reload={reloadStats} />}
          {active === "approvals" && <ApprovalsPanel lang={lang} onChange={reloadStats} />}
          {active === "invites"   && <InvitesPanel lang={lang} />}
          {active === "users"     && <UsersPanel lang={lang} currentAdmin={user} />}
          {active === "bookings"  && <BookingsPanel lang={lang} />}
          {active === "payments"  && <PaymentsPanel lang={lang} />}
          {active === "reports"   && <ReportsPanel lang={lang} onChange={reloadStats} />}
          {active === "chat"      && <ChatModPanel lang={lang} />}
          {active === "analytics" && <AnalyticsPanel lang={lang} />}
          {active === "marketplace" && <AdminMarketplace />}
        </div>
      </div>
    </Layout>
  );
}

/* ---------- STATS / OVERVIEW ---------- */
function StatsPanel({ lang, stats, reload }) {
  const [busy, setBusy] = useState(false);
  const refresh = async () => { setBusy(true); await reload(); setBusy(false); };
  if (!stats) return <div className="surface-elevated p-8 text-center text-white/45 font-mono text-xs">Loading...</div>;
  return (
    <div className="space-y-5" data-testid="admin-stats">
      <div className="flex items-center justify-between">
        <div className="label-mono">{lang === "bg" ? "// В реално време" : "// Real-time"}</div>
        <button onClick={refresh} disabled={busy} className="btn-secondary px-3 py-1.5 text-[10px] flex items-center gap-1.5"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {lang === "bg" ? "Обнови" : "Refresh"}</button>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label={lang === "bg" ? "Треньори" : "Coaches"} value={stats.users.coaches} icon={Trophy} />
        <StatTile label={lang === "bg" ? "Родители" : "Parents"} value={stats.users.parents} icon={User} />
        <StatTile label={lang === "bg" ? "Играчи" : "Players"} value={stats.users.players} icon={Users} />
        <StatTile label={lang === "bg" ? "Академии" : "Academies"} value={stats.users.academies} icon={Building2} />
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label={lang === "bg" ? "Резервации" : "Bookings"} value={stats.bookings.total} icon={CreditCard} sub={`${stats.bookings.paid} paid`} />
        <StatTile label={lang === "bg" ? "Приходи" : "Revenue"} value={`€${stats.bookings.revenue_usd}`} icon={DollarSign} accent />
        <StatTile label={lang === "bg" ? "Сигнали" : "Open reports"} value={stats.reports_open} icon={Flag} danger={stats.reports_open > 0} />
        <StatTile label={lang === "bg" ? "Онлайн" : "Online now"} value={stats.online_now} icon={Activity} accent />
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <StatTile label={lang === "bg" ? "Чакат одобрение" : "Pending approval"} value={stats.users.pending || 0} icon={UserCheck} danger={(stats.users.pending || 0) > 0} />
        <StatTile label={lang === "bg" ? "Спрени акаунти" : "Suspended"} value={stats.users.suspended || 0} icon={Ban} />
        <StatTile label={lang === "bg" ? "Общо потребители" : "Total users"} value={stats.users.total} icon={Sparkles} />
      </div>

      {/* PRO + Lead-gen + Invites — Iter 31 */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile
          label={lang === "bg" ? "PRO активни" : "Active PRO"}
          value={stats.users.pro_total || 0}
          sub={`${stats.users.pro_coaches || 0} coach · ${stats.users.pro_academies || 0} academy`}
          icon={Sparkles}
          accent
        />
        <StatTile
          label={lang === "bg" ? "Заявки общо" : "Applications"}
          value={stats.applications?.total || 0}
          sub={`${stats.applications?.new || 0} ${lang === "bg" ? "нови" : "new"}`}
          icon={Activity}
          danger={(stats.applications?.new || 0) > 0}
        />
        <StatTile
          label={lang === "bg" ? "Покани активни" : "Invites active"}
          value={stats.invites?.active || 0}
          sub={`${stats.invites?.total || 0} total`}
          icon={Sparkles}
        />
        <StatTile
          label={lang === "bg" ? "Покани използвани" : "Invites redeemed"}
          value={stats.invites?.redeemed || 0}
          icon={UserCheck}
          accent={(stats.invites?.redeemed || 0) > 0}
        />
      </div>
      {stats.users.suspended > 0 && (
        <div className="surface-1 p-4 flex items-center gap-3 border-amber-500/30">
          <AlertTriangle className="w-5 h-5 text-amber-400" />
          <div className="text-sm">
            <span className="font-mono">{stats.users.suspended}</span> {lang === "bg" ? "акаунта са спрени." : "accounts currently suspended."}
          </div>
        </div>
      )}
    </div>
  );
}

function StatTile({ label, value, icon: Icon, sub, danger, accent }) {
  return (
    <div className="stat-tile">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="stat-label">{label}</div>
          <div className={`stat-number mt-2 ${danger ? "text-red-400" : accent ? "text-pitch text-glow-emerald" : ""}`}>{value}</div>
          {sub && <div className="text-[10px] font-mono text-white/40 mt-1">{sub}</div>}
        </div>
        <div className="w-9 h-9 rounded-full bg-pitch/10 border border-pitch/30 flex items-center justify-center flex-shrink-0">
          <Icon className="w-3.5 h-3.5 text-pitch" />
        </div>
      </div>
    </div>
  );
}

/* ---------- APPROVALS ---------- */
function ApprovalsPanel({ lang, onChange }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/approvals"); setList(data); } catch (_) { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const approve = async (u) => {
    try { await api.put(`/admin/users/${u.id}/approve`); setList((l) => l.filter((x) => x.id !== u.id)); onChange?.(); toast.success(lang === "bg" ? "Одобрен" : "Approved"); }
    catch (_) { toast.error("Error"); }
  };
  const reject = async (u) => {
    const reason = window.prompt(lang === "bg" ? "Причина за отказ (по избор):" : "Rejection reason (optional):") || "";
    try { await api.put(`/admin/users/${u.id}/reject`, { reason }); setList((l) => l.filter((x) => x.id !== u.id)); onChange?.(); toast.success(lang === "bg" ? "Отказан" : "Rejected"); }
    catch (_) { toast.error("Error"); }
  };

  return (
    <div className="space-y-4" data-testid="admin-approvals">
      <div className="flex items-center justify-between">
        <div className="label-mono">// {lang === "bg" ? "Чакащи одобрение" : "Awaiting approval"}</div>
        <button onClick={load} disabled={busy} className="btn-secondary px-3 py-1.5 text-[10px] flex items-center gap-1.5"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {lang === "bg" ? "Обнови" : "Refresh"}</button>
      </div>

      {list.length === 0 ? (
        <div className="surface-elevated p-12 text-center" data-testid="approvals-empty">
          <UserCheck className="w-10 h-10 text-pitch/40 mx-auto mb-3" />
          <div className="font-oswald uppercase text-lg tracking-tight">{lang === "bg" ? "Нямаш чакащи" : "All caught up"}</div>
          <div className="text-white/35 text-xs font-mono mt-1">
            {lang === "bg" ? "Нови регистрации ще се появят тук." : "New coach / academy signups land here."}
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {list.map((u) => (
            <div key={u.id} className="surface-1 p-4" data-testid={`approval-${u.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-12 h-12 rounded-full bg-pitch/15 text-pitch flex items-center justify-center font-oswald text-lg flex-shrink-0">{u.name?.[0]}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-oswald uppercase tracking-tight">{u.name}</span>
                    <span className="chip-badge">{u.role}</span>
                    {u.role === "academy" && u.academy_size && <span className="chip-badge gold">{u.academy_size}</span>}
                  </div>
                  <div className="text-[10px] font-mono text-white/45 mt-0.5 truncate">{u.email}</div>
                  <div className="text-[11px] font-mono text-white/55 mt-1.5 flex flex-wrap gap-x-3 gap-y-1">
                    {u.city && <span>📍 {u.city}</span>}
                    {u.specialty && <span>· {u.specialty}</span>}
                    {u.hourly_rate != null && <span>· €{u.hourly_rate}/h</span>}
                    {u.phone && <span>· ☎ {u.phone}</span>}
                  </div>
                  {u.bio && <div className="text-xs text-white/55 mt-2 line-clamp-2">{u.bio}</div>}
                  {(u.verification_docs || []).length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-1.5" data-testid={`approval-docs-${u.id}`}>
                      {(u.verification_docs || []).map((d) => (
                        <a
                          key={d.id}
                          href={`${d.url}?auth=${localStorage.getItem("auth_token") || ""}`}
                          target="_blank"
                          rel="noreferrer"
                          className="inline-flex items-center gap-1.5 px-2 py-1 text-[9px] font-mono uppercase tracking-widest border border-pitch/40 bg-pitch/10 text-pitch hover:bg-pitch/20 rounded"
                          data-testid={`approval-doc-${d.id}`}
                          title={d.filename}
                        >
                          📎 {d.type}
                        </a>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex gap-2 mt-3">
                <button onClick={() => approve(u)} className="flex-1 btn-primary py-2 text-xs flex items-center justify-center gap-1.5" data-testid={`approve-${u.id}`}>
                  <Check className="w-3.5 h-3.5" /> {lang === "bg" ? "Одобри" : "Approve"}
                </button>
                <button onClick={() => reject(u)} className="flex-1 btn-secondary py-2 text-xs flex items-center justify-center gap-1.5 text-red-400 border-red-500/30 hover:bg-red-500/10" data-testid={`reject-${u.id}`}>
                  <X className="w-3.5 h-3.5" /> {lang === "bg" ? "Откажи" : "Reject"}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- USERS ---------- */
function UsersPanel({ lang, currentAdmin }) {
  const [users, setUsers] = useState([]);
  const [q, setQ] = useState("");
  const [role, setRole] = useState("");
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const params = {};
      if (q) params.q = q;
      if (role) params.role = role;
      if (status) params.status = status;
      const { data } = await api.get("/admin/users", { params });
      setUsers(data);
    } catch (_) { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [q, role, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleBadge = async (u, badge) => {
    const cur = new Set(u.badges || []);
    cur.has(badge) ? cur.delete(badge) : cur.add(badge);
    try {
      const { data } = await api.put(`/admin/users/${u.id}/badges`, { badges: Array.from(cur) });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, badges: data.badges || Array.from(cur) } : x)));
      toast.success(lang === "bg" ? "Значки обновени" : "Badges updated");
    } catch (_) { toast.error("Error"); }
  };

  const toggleSuspend = async (u) => {
    const active = u.is_active === false;
    if (!active && !window.confirm(lang === "bg" ? "Сигурен ли си?" : "Are you sure?")) return;
    try {
      await api.put(`/admin/users/${u.id}/suspend`, { active });
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, is_active: active } : x)));
      toast.success(active ? (lang === "bg" ? "Активиран" : "Activated") : (lang === "bg" ? "Спрян" : "Suspended"));
    } catch (_) { toast.error("Error"); }
  };

  const flag = async (u, path, body, patch) => {
    try {
      await api.put(`/admin/users/${u.id}/${path}`, body);
      setUsers((us) => us.map((x) => (x.id === u.id ? { ...x, ...patch } : x)));
      toast.success(lang === "bg" ? "Обновено" : "Updated");
    } catch (_) { toast.error("Error"); }
  };

  const del = async (u) => {
    if (!window.confirm(lang === "bg" ? `Изтрий ${u.name}? Това е необратимо.` : `Delete ${u.name}? This is permanent.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      setUsers((us) => us.filter((x) => x.id !== u.id));
      toast.success(lang === "bg" ? "Изтрит" : "Deleted");
    } catch (_) { toast.error("Error"); }
  };

  const statusChip = (u) => {
    if (u.is_active === false) return <span className="chip-badge amber">SUSPENDED</span>;
    if (u.approval_status === "pending") return <span className="chip-badge amber">PENDING</span>;
    if (u.approval_status === "rejected") return <span className="chip-badge" style={{ color: "#f87171", borderColor: "rgba(248,113,113,0.35)" }}>REJECTED</span>;
    return <span className="chip-badge">ACTIVE</span>;
  };

  return (
    <div className="space-y-4" data-testid="admin-users">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        <div className="relative md:col-span-2">
          <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder={lang === "bg" ? "Търси име, имейл, телефон, град..." : "Search name, email, phone, city..."}
            className="w-full bg-[#0A1628] border border-white/10 pl-9 pr-3 py-2.5 focus:border-pitch outline-none"
            data-testid="admin-search"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={role} onChange={(e) => setRole(e.target.value)} className="bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none" data-testid="admin-role-filter">
            <option value="">{lang === "bg" ? "Всички роли" : "All roles"}</option>
            <option value="coach">Coach</option>
            <option value="parent">Parent</option>
            <option value="player">Player</option>
            <option value="academy">Academy</option>
          </select>
          <select value={status} onChange={(e) => setStatus(e.target.value)} className="bg-[#0A1628] border border-white/10 px-3 py-2.5 focus:border-pitch outline-none" data-testid="admin-status-filter">
            <option value="">{lang === "bg" ? "Всички статуси" : "All status"}</option>
            <option value="active">{lang === "bg" ? "Активни" : "Active"}</option>
            <option value="suspended">{lang === "bg" ? "Спрени" : "Suspended"}</option>
            <option value="pending">{lang === "bg" ? "Чакащи" : "Pending"}</option>
            <option value="rejected">{lang === "bg" ? "Отказани" : "Rejected"}</option>
          </select>
        </div>
      </div>

      <div className="surface-elevated overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-white/40 border-b border-white/8">
              <tr>
                <th className="text-left p-3">User</th>
                <th className="text-left p-3 hidden md:table-cell">Role</th>
                <th className="text-left p-3 hidden md:table-cell">Badges</th>
                <th className="text-left p-3 hidden lg:table-cell">City</th>
                <th className="text-left p-3 hidden lg:table-cell">Phone</th>
                <th className="text-left p-3">Status</th>
                <th className="text-right p-3">Actions</th>
              </tr>
            </thead>
            <tbody data-testid="admin-users-tbody">
              {users.length === 0 ? (
                <tr><td colSpan={7} className="p-8 text-center text-white/35 font-mono text-xs">{busy ? "Loading..." : (lang === "bg" ? "Няма резултати" : "No results")}</td></tr>
              ) : users.map((u) => (
                <tr key={u.id} className={`border-b border-white/5 hover:bg-white/[0.03] transition-colors ${u.is_active === false ? "opacity-50" : ""}`} data-testid={`admin-user-${u.id}`}>
                  <td className="p-3">
                    <div className="flex items-center gap-2.5">
                      <div className="w-8 h-8 rounded-full bg-pitch/15 text-pitch flex items-center justify-center font-oswald text-sm">{u.name?.[0]}</div>
                      <div className="min-w-0">
                        <div className="font-oswald uppercase tracking-tight truncate text-sm">{u.name}</div>
                        <div className="text-[10px] font-mono text-white/40 truncate">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <span className="chip-badge">{u.role}</span>
                    {u.is_admin && <span className="chip-badge gold ml-1">ADMIN</span>}
                    {u.is_test && <span className="chip-badge amber ml-1" data-testid={`test-chip-${u.id}`}>TEST</span>}
                    {u.is_sponsored && <span className="chip-badge ml-1" data-testid={`sponsored-chip-${u.id}`}>SPONSORED</span>}
                  </td>
                  <td className="p-3 hidden md:table-cell">
                    <div className="flex flex-wrap gap-1">
                      {VALID_BADGES.map((b) => {
                        const has = (u.badges || []).includes(b);
                        return (
                          <button
                            key={b}
                            onClick={() => toggleBadge(u, b)}
                            className={`px-2 py-0.5 text-[9px] font-mono uppercase tracking-widest border rounded transition-all ${
                              has ? "border-pitch bg-pitch/10 text-pitch" : "border-white/10 text-white/35 hover:border-pitch/50"
                            }`}
                            data-testid={`badge-${b}-${u.id}`}
                            title={b}
                          >
                            {b.replace("_", " ").replace("uefa", "uefa ").trim()}
                          </button>
                        );
                      })}
                    </div>
                  </td>
                  <td className="p-3 hidden lg:table-cell text-xs font-mono text-white/45">{u.city || "—"}</td>
                  <td className="p-3 hidden lg:table-cell text-xs font-mono text-white/45">{u.phone || "—"}</td>
                  <td className="p-3">{statusChip(u)}</td>
                  <td className="p-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {["coach", "academy"].includes(u.role) && (
                        <button onClick={() => flag(u, "sponsored", { sponsored: !u.is_sponsored }, { is_sponsored: !u.is_sponsored })} className={`p-1.5 hover:text-pitch ${u.is_sponsored ? "text-pitch" : "text-white/50"}`} title={lang === "bg" ? "Спонсориран профил" : "Sponsored listing"} data-testid={`sponsor-${u.id}`}>
                          <Megaphone className="w-3.5 h-3.5" />
                        </button>
                      )}
                      {!u.is_admin && (
                        <button onClick={() => flag(u, "test-flag", { is_test: !u.is_test }, { is_test: !u.is_test })} className={`p-1.5 hover:text-amber-400 ${u.is_test ? "text-amber-400" : "text-white/50"}`} title={lang === "bg" ? "Тестов акаунт (скрит публично)" : "Test account (hidden publicly)"} data-testid={`testflag-${u.id}`}>
                          <FlaskConical className="w-3.5 h-3.5" />
                        </button>
                      )}
                      <button onClick={() => toggleSuspend(u)} disabled={u.id === currentAdmin.id} className="p-1.5 text-white/50 hover:text-amber-400 disabled:opacity-30" title={u.is_active === false ? "Activate" : "Suspend"} data-testid={`suspend-${u.id}`}>
                        <Ban className="w-3.5 h-3.5" />
                      </button>
                      <button onClick={() => del(u)} disabled={u.id === currentAdmin.id} className="p-1.5 text-white/50 hover:text-red-400 disabled:opacity-30" title="Delete" data-testid={`delete-${u.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ---------- BOOKINGS ---------- */
function BookingsPanel({ lang }) {
  const [list, setList] = useState([]);
  useEffect(() => { api.get("/admin/bookings").then((r) => setList(r.data)).catch(() => {}); }, []);
  return (
    <div className="surface-elevated overflow-hidden" data-testid="admin-bookings">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-white/40 border-b border-white/8">
            <tr>
              <th className="text-left p-3">Coach → Parent</th>
              <th className="text-left p-3 hidden md:table-cell">Slot</th>
              <th className="text-left p-3 hidden md:table-cell">Duration</th>
              <th className="text-left p-3">Amount</th>
              <th className="text-left p-3">Status</th>
            </tr>
          </thead>
          <tbody>
            {list.length === 0 ? (
              <tr><td colSpan={5} className="p-8 text-center text-white/35 font-mono text-xs">{lang === "bg" ? "Няма резервации" : "No bookings"}</td></tr>
            ) : list.map((b) => (
              <tr key={b.id} className="border-b border-white/5 hover:bg-white/[0.03]">
                <td className="p-3">
                  <div className="text-sm">
                    <span className="font-oswald uppercase tracking-tight">{b.coach?.name || "—"}</span>
                    <span className="text-white/30 mx-1">→</span>
                    <span className="font-oswald uppercase tracking-tight">{b.parent?.name || "—"}</span>
                  </div>
                  <div className="text-[10px] font-mono text-white/35">{new Date(b.created_at).toLocaleString()}</div>
                </td>
                <td className="p-3 hidden md:table-cell text-xs font-mono text-white/55">{b.slot_at ? new Date(b.slot_at).toLocaleString() : "—"}</td>
                <td className="p-3 hidden md:table-cell text-xs font-mono">{b.duration_min}min</td>
                <td className="p-3 font-oswald text-pitch">€{b.amount}</td>
                <td className="p-3"><span className={`chip-badge ${b.status === "confirmed" || b.status === "paid" ? "" : "amber"}`}>{b.status?.toUpperCase()}</span></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- PAYMENTS ---------- */
function PaymentsPanel({ lang }) {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/admin/payments").then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="surface-elevated p-8 text-center text-white/45 font-mono text-xs">Loading...</div>;
  const { transactions, pro_subscribers, summary } = data;
  return (
    <div className="space-y-4" data-testid="admin-payments">
      <div className="grid grid-cols-3 gap-3">
        <StatTile label={lang === "bg" ? "Транзакции" : "Transactions"} value={summary.transactions_total} icon={CreditCard} />
        <StatTile label={lang === "bg" ? "Платено" : "Paid revenue"} value={`€${summary.paid_revenue_usd}`} icon={DollarSign} accent />
        <StatTile label={lang === "bg" ? "PRO абонати" : "Pro subs"} value={summary.pro_count} icon={Sparkles} accent />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1.6fr_1fr] gap-4">
        {/* Transactions */}
        <div className="surface-elevated overflow-hidden">
          <div className="p-3 border-b border-white/8 label-mono">// {lang === "bg" ? "Транзакции" : "Transactions"}</div>
          <div className="overflow-x-auto max-h-[520px] scroll-thin">
            <table className="w-full text-sm">
              <thead className="bg-white/[0.03] text-[10px] font-mono uppercase tracking-widest text-white/40 border-b border-white/8 sticky top-0">
                <tr>
                  <th className="text-left p-3">User</th>
                  <th className="text-left p-3">Plan</th>
                  <th className="text-left p-3">Amount</th>
                  <th className="text-left p-3">Status</th>
                </tr>
              </thead>
              <tbody>
                {transactions.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-white/35 font-mono text-xs">{lang === "bg" ? "Няма транзакции" : "No transactions"}</td></tr>
                ) : transactions.map((t) => (
                  <tr key={t.id} className="border-b border-white/5" data-testid={`payment-${t.id}`}>
                    <td className="p-3">
                      <div className="font-oswald uppercase tracking-tight text-sm">{t.user?.name || "—"}</div>
                      <div className="text-[10px] font-mono text-white/35">{t.user?.email || ""}</div>
                    </td>
                    <td className="p-3"><span className="chip-badge">{t.plan}</span></td>
                    <td className="p-3 font-oswald text-pitch">€{t.amount}</td>
                    <td className="p-3"><span className={`chip-badge ${t.payment_status === "paid" ? "" : "amber"}`}>{(t.payment_status || "unpaid").toUpperCase()}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pro Subscribers */}
        <div className="surface-elevated overflow-hidden">
          <div className="p-3 border-b border-white/8 label-mono">// {lang === "bg" ? "Pro абонати" : "Pro subscribers"}</div>
          <div className="max-h-[520px] overflow-y-auto scroll-thin">
            {pro_subscribers.length === 0 ? (
              <div className="p-8 text-center text-white/35 font-mono text-xs">{lang === "bg" ? "Няма още" : "None yet"}</div>
            ) : pro_subscribers.map((p) => (
              <div key={p.id} className="p-3 border-b border-white/5 flex items-center gap-3" data-testid={`pro-${p.id}`}>
                <div className="w-8 h-8 rounded-full bg-pitch text-white font-oswald flex items-center justify-center text-xs">{p.name?.[0]}</div>
                <div className="min-w-0 flex-1">
                  <div className="font-oswald uppercase tracking-tight truncate text-sm">{p.name}</div>
                  <div className="text-[10px] font-mono text-white/40 truncate">{p.email}</div>
                </div>
                <Sparkles className="w-3.5 h-3.5 text-pitch" />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ---------- REPORTS ---------- */
function ReportsPanel({ lang, onChange }) {
  const [list, setList] = useState([]);
  const [statusF, setStatusF] = useState("open");
  const load = async () => {
    try {
      const params = statusF ? { status: statusF } : {};
      const { data } = await api.get("/admin/reports", { params });
      setList(data);
    } catch (_) { /* ignore */ }
  };
  useEffect(() => { load(); }, [statusF]); // eslint-disable-line react-hooks/exhaustive-deps

  const resolve = async (r, status) => {
    try {
      await api.put(`/admin/reports/${r.id}`, { status });
      setList((xs) => xs.filter((x) => x.id !== r.id));
      onChange?.();
      toast.success(status === "resolved" ? (lang === "bg" ? "Решен" : "Resolved") : (lang === "bg" ? "Отхвърлен" : "Dismissed"));
    } catch (_) { toast.error("Error"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-reports">
      <div className="flex gap-2">
        {["open", "resolved", "dismissed", ""].map((s) => (
          <button key={s || "all"} onClick={() => setStatusF(s)} className={`chip-badge ${statusF === s ? "" : "opacity-50"}`}
                  style={statusF === s ? {} : { background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.12)", color: "rgba(255,255,255,0.55)" }}>
            {s || (lang === "bg" ? "Всички" : "All")}
          </button>
        ))}
      </div>
      {list.length === 0 ? (
        <div className="surface-elevated p-12 text-center text-white/35 font-mono text-xs">
          <Flag className="w-10 h-10 text-white/20 mx-auto mb-3" />
          {lang === "bg" ? "Няма сигнали" : "No reports"}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((r) => (
            <div key={r.id} className="surface-1 p-4 flex flex-col md:flex-row md:items-center gap-3" data-testid={`report-${r.id}`}>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="chip-badge amber">{r.context?.toUpperCase()}</span>
                  <span className="chip-badge">{r.status?.toUpperCase()}</span>
                  <span className="text-[10px] font-mono text-white/35">{new Date(r.created_at).toLocaleString()}</span>
                </div>
                <div className="mt-2 text-sm">
                  <span className="text-white/65 font-mono">{r.reporter_name}</span>
                  <span className="text-white/30 mx-1.5">→</span>
                  <span className="font-oswald uppercase tracking-tight">{r.reported_name}</span>
                </div>
                <div className="text-sm text-white/85 mt-1">{r.reason}</div>
                {r.details && <div className="text-xs text-white/45 mt-1 italic line-clamp-2">{r.details}</div>}
              </div>
              {r.status === "open" && (
                <div className="flex gap-2 flex-shrink-0">
                  <button onClick={() => resolve(r, "resolved")} className="btn-primary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`resolve-${r.id}`}><Check className="w-3 h-3" /> {lang === "bg" ? "Реши" : "Resolve"}</button>
                  <button onClick={() => resolve(r, "dismissed")} className="btn-secondary px-3 py-1.5 text-xs flex items-center gap-1" data-testid={`dismiss-${r.id}`}><X className="w-3 h-3" /> {lang === "bg" ? "Отхвърли" : "Dismiss"}</button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- CHAT MODERATION ---------- */
function ChatModPanel({ lang }) {
  const [list, setList] = useState([]);
  const [onlyReported, setOnlyReported] = useState(true);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    setBusy(true);
    try {
      const { data } = await api.get("/admin/messages", { params: { only_reported: onlyReported } });
      setList(data);
    } catch (_) { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, [onlyReported]); // eslint-disable-line react-hooks/exhaustive-deps

  const del = async (m) => {
    if (!window.confirm(lang === "bg" ? "Изтрий това съобщение?" : "Delete this message?")) return;
    try {
      await api.delete(`/admin/messages/${m.id}`);
      setList((xs) => xs.filter((x) => x.id !== m.id));
      toast.success(lang === "bg" ? "Изтрито" : "Deleted");
    } catch (_) { toast.error("Error"); }
  };

  return (
    <div className="space-y-3" data-testid="admin-chat">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex gap-2">
          <button onClick={() => setOnlyReported(true)}  className={`chip-badge ${onlyReported ? "" : "opacity-50"}`} data-testid="chat-flag-only">
            {lang === "bg" ? "Само сигнализирани" : "Reported only"}
          </button>
          <button onClick={() => setOnlyReported(false)} className={`chip-badge ${!onlyReported ? "" : "opacity-50"}`} data-testid="chat-all">
            {lang === "bg" ? "Последни 200" : "Latest 200"}
          </button>
        </div>
        <button onClick={load} disabled={busy} className="btn-secondary px-3 py-1.5 text-[10px] flex items-center gap-1.5"><RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {lang === "bg" ? "Обнови" : "Refresh"}</button>
      </div>

      {list.length === 0 ? (
        <div className="surface-elevated p-12 text-center text-white/35 font-mono text-xs">
          <MessageSquareWarning className="w-10 h-10 text-white/20 mx-auto mb-3" />
          {lang === "bg" ? "Чисто. Няма съмнителни съобщения." : "All clear. No flagged messages."}
        </div>
      ) : (
        <div className="space-y-2">
          {list.map((m) => (
            <div key={m.id} className="surface-1 p-4" data-testid={`chat-msg-${m.id}`}>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-pitch/15 text-pitch flex items-center justify-center font-oswald text-xs flex-shrink-0">{m.sender_name?.[0]}</div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap mb-1">
                    <span className="font-oswald uppercase tracking-tight text-sm">{m.sender_name}</span>
                    <span className="text-[10px] font-mono text-white/35">{new Date(m.created_at).toLocaleString()}</span>
                    {(m.reports || []).length > 0 && <span className="chip-badge amber">{m.reports.length} {lang === "bg" ? "сигнал(и)" : "report(s)"}</span>}
                  </div>
                  {m.content && <div className="text-sm text-white/85 break-words">{m.content}</div>}
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="text-[10px] font-mono text-white/45 mt-1">+ {m.attachments.length} attachment(s)</div>
                  )}
                </div>
                <button onClick={() => del(m)} className="p-1.5 text-white/50 hover:text-red-400 flex-shrink-0" title="Soft-delete" data-testid={`chat-delete-${m.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------- ANALYTICS ---------- */
function AnalyticsPanel({ lang }) {
  const [data, setData] = useState(null);
  const [series, setSeries] = useState("signups");
  useEffect(() => { api.get("/admin/analytics", { params: { weeks: 8 } }).then((r) => setData(r.data)).catch(() => {}); }, []);
  if (!data) return <div className="surface-elevated p-8 text-center text-white/45 font-mono text-xs">Loading...</div>;
  return (
    <div className="space-y-4" data-testid="admin-analytics">
      <div className="grid grid-cols-3 gap-3">
        <StatTile label={lang === "bg" ? "Регистрации (8 седм.)" : "Signups (8w)"} value={data.totals.signups} icon={UserCheck} />
        <StatTile label={lang === "bg" ? "Резервации (8 седм.)" : "Bookings (8w)"} value={data.totals.bookings} icon={CreditCard} />
        <StatTile label={lang === "bg" ? "Приходи (8 седм.)" : "Revenue (8w)"} value={`€${data.totals.revenue}`} icon={DollarSign} accent />
      </div>
      <div className="card-sharp p-4 md:p-6">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="label-mono">// {lang === "bg" ? "Седмичен ритъм" : "Weekly cadence"}</div>
          <div className="inline-flex border border-white/10 p-0.5 rounded-sm">
            {["signups", "bookings", "revenue"].map((k) => (
              <button key={k} onClick={() => setSeries(k)} className={`px-3 py-1.5 text-[10px] font-mono uppercase tracking-widest ${series === k ? "bg-pitch/15 text-pitch" : "text-white/45 hover:text-white"}`} data-testid={`analytics-tab-${k}`}>
                {k}
              </button>
            ))}
          </div>
        </div>
        <Sparkline data={data.weeks} series={series} />
      </div>
    </div>
  );
}

function Sparkline({ data, series }) {
  const W = 720, H = 180, PAD = { l: 28, r: 12, t: 16, b: 24 };
  const innerW = W - PAD.l - PAD.r, innerH = H - PAD.t - PAD.b;
  const values = data.map((d) => Number(d[series]) || 0);
  const maxV = Math.max(1, ...values);
  const stepX = data.length > 1 ? innerW / (data.length - 1) : 0;
  const points = data.map((d, i) => ({
    x: PAD.l + i * stepX,
    y: PAD.t + innerH - ((Number(d[series]) || 0) / maxV) * innerH,
    v: Number(d[series]) || 0,
    label: d.label,
  }));
  const path = points.map((p, i) => (i === 0 ? `M ${p.x},${p.y}` : `L ${p.x},${p.y}`)).join(" ");
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      <defs>
        <linearGradient id="adm-area" x1="0" x2="0" y1="0" y2="1">
          <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.35" />
          <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
        </linearGradient>
      </defs>
      {[0, 0.25, 0.5, 0.75, 1].map((p, i) => (
        <line key={i} x1={PAD.l} x2={W - PAD.r} y1={PAD.t + innerH * p} y2={PAD.t + innerH * p} stroke="rgba(255,255,255,0.05)" />
      ))}
      <path d={`${path} L ${points[points.length - 1]?.x || 0},${PAD.t + innerH} L ${points[0]?.x || 0},${PAD.t + innerH} Z`} fill="url(#adm-area)" />
      <path d={path} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={p.x} cy={p.y} r="3" fill="#0A1628" stroke="#3B82F6" strokeWidth="2" />
          <text x={p.x} y={H - 6} textAnchor="middle" style={{ fontSize: 10, fill: "rgba(255,255,255,0.45)", fontFamily: "monospace" }}>{p.label}</text>
          <text x={p.x} y={p.y - 8} textAnchor="middle" style={{ fontSize: 9, fill: "rgba(59,130,246,0.85)", fontFamily: "monospace" }}>{p.v}</text>
        </g>
      ))}
    </svg>
  );
}


/* ---------- INVITES (referral links / free Pro for early adopters) ---------- */
function InvitesPanel({ lang }) {
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [showCreate, setShowCreate] = useState(false);
  const [filter, setFilter] = useState("all"); // all | active | used | exhausted | disabled

  const load = async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/invites"); setList(data); } catch (_) { /* ignore */ }
    setBusy(false);
  };
  useEffect(() => { load(); }, []);

  const remove = async (iid) => {
    if (!window.confirm(lang === "bg" ? "Изтрий тази покана?" : "Delete this invite?")) return;
    try {
      await api.delete(`/admin/invites/${iid}`);
      setList((l) => l.filter((x) => x.id !== iid));
      toast.success(lang === "bg" ? "Изтрита" : "Deleted");
    } catch (_) { toast.error("Error"); }
  };

  const toggleActive = async (inv) => {
    try {
      await api.put(`/admin/invites/${inv.id}`, { active: !inv.active });
      setList((l) => l.map((x) => x.id === inv.id ? { ...x, active: !inv.active } : x));
      toast.success(lang === "bg" ? "Обновена" : "Updated");
    } catch (_) { toast.error("Error"); }
  };

  const copyToClipboard = (text, msg) => {
    if (navigator.clipboard?.writeText) {
      navigator.clipboard.writeText(text).then(() => toast.success(msg)).catch(() => {});
    }
  };

  const buildShareUrl = (code) => `${window.location.origin}/register?invite=${code}`;

  return (
    <div className="space-y-4" data-testid="admin-invites">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">// {lang === "bg" ? "Покани за безплатен Pro" : "Free Pro invites"}</div>
          <p className="text-xs text-white/45 font-mono mt-1 max-w-xl">
            {lang === "bg"
              ? "Генерирай линкове и кодове, които автоматично активират Pro достъп при регистрация."
              : "Generate links + codes that automatically unlock Pro on signup."}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} disabled={busy} className="btn-secondary px-3 py-1.5 text-[10px] flex items-center gap-1.5" data-testid="invites-refresh">
            <RefreshCw className={`w-3 h-3 ${busy ? "animate-spin" : ""}`} /> {lang === "bg" ? "Обнови" : "Refresh"}
          </button>
          <button onClick={() => setShowCreate(true)} className="btn-primary px-4 py-2 text-[11px] flex items-center gap-1.5" data-testid="invites-create-btn">
            <Sparkles className="w-3.5 h-3.5" /> {lang === "bg" ? "Нова покана" : "New invite"}
          </button>
        </div>
      </div>

      {/* Aggregate analytics + filter chips */}
      <InviteAnalyticsRow list={list} lang={lang} filter={filter} setFilter={setFilter} />

      {list.length === 0 ? (
        <div className="surface-elevated p-12 text-center" data-testid="invites-empty">
          <Ticket className="w-10 h-10 text-pitch/40 mx-auto mb-3" />
          <div className="font-oswald uppercase text-lg tracking-tight">
            {lang === "bg" ? "Все още няма покани" : "No invites yet"}
          </div>
          <div className="text-white/35 text-xs font-mono mt-1">
            {lang === "bg" ? "Натисни \"Нова покана\", за да създадеш първата." : "Tap \"New invite\" to create your first."}
          </div>
        </div>
      ) : (
        <div className="space-y-2">
          {list
            .filter((inv) => {
              if (filter === "all") return true;
              const exhausted = inv.max_uses && (inv.used_count || 0) >= inv.max_uses;
              if (filter === "disabled") return !inv.active;
              if (filter === "exhausted") return inv.active && exhausted;
              if (filter === "active") return inv.active && !exhausted;
              if (filter === "used") return (inv.used_count || 0) > 0;
              return true;
            })
            .map((inv) => {
            const shareUrl = buildShareUrl(inv.code);
            const limit = inv.max_uses ? `${inv.used_count || 0}/${inv.max_uses}` : `${inv.used_count || 0}/∞`;
            const exhausted = inv.max_uses && (inv.used_count || 0) >= inv.max_uses;
            const proLabel = inv.pro_duration_days
              ? (lang === "bg" ? `${inv.pro_duration_days} дни Pro` : `${inv.pro_duration_days}d Pro`)
              : (lang === "bg" ? "Доживотен Pro" : "Lifetime Pro");
            const shareMsg = lang === "bg"
              ? `🟢 Покана за ONTHEPITCH PRO — натисни линка и регистрирай се безплатно: ${shareUrl}`
              : `🟢 You're invited to ONTHEPITCH PRO — click to claim free access: ${shareUrl}`;
            const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(shareMsg)}`;
            const messengerUrl = `https://www.facebook.com/dialog/send?app_id=0&link=${encodeURIComponent(shareUrl)}&redirect_uri=${encodeURIComponent(window.location.origin)}`;
            const emailUrl = `mailto:?subject=${encodeURIComponent("ONTHEPITCH — Free PRO invite")}&body=${encodeURIComponent(shareMsg)}`;
            return (
              <div key={inv.id} className="surface-1 p-4 rounded" data-testid={`invite-row-${inv.id}`}>
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <code className="font-mono text-pitch text-base tracking-wider px-2.5 py-1 bg-pitch/10 border border-pitch/30 rounded" data-testid={`invite-code-${inv.id}`}>
                        {inv.code}
                      </code>
                      {!inv.active ? (
                        <span className="chip-badge text-[9px]">{lang === "bg" ? "Спряна" : "Disabled"}</span>
                      ) : exhausted ? (
                        <span className="chip-badge text-[9px]" style={{ borderColor: "rgba(245, 158, 11, 0.4)", color: "#fbbf24" }} data-testid={`invite-exhausted-${inv.id}`}>{lang === "bg" ? "Изчерпана" : "Exhausted"}</span>
                      ) : (
                        <span className="chip-badge gold text-[9px]">{lang === "bg" ? "Активна" : "Active"}</span>
                      )}
                      <span className="chip-badge">
                        {inv.pro_duration_days ? <span>{proLabel}</span> : <span className="inline-flex items-center gap-1"><InfinityIcon className="w-3 h-3" /> {proLabel}</span>}
                      </span>
                      {inv.role_hint && <span className="chip-badge text-[9px]">{inv.role_hint.toUpperCase()}</span>}
                      <span className="text-[10px] font-mono text-white/45">{lang === "bg" ? "Употреби: " : "Uses: "}<span className="text-white/70">{limit}</span></span>
                    </div>
                    {inv.note && <div className="text-xs text-white/55 mt-2">{inv.note}</div>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <input
                        readOnly
                        value={shareUrl}
                        className="flex-1 min-w-[200px] bg-black/40 border border-white/8 px-2 py-1.5 text-[11px] font-mono text-white/65 rounded select-all"
                        data-testid={`invite-url-${inv.id}`}
                      />
                      <button
                        onClick={() => copyToClipboard(shareUrl, lang === "bg" ? "Линкът е копиран" : "Link copied")}
                        className="px-2.5 py-1.5 border border-white/15 hover:border-pitch hover:text-pitch text-xs font-mono inline-flex items-center gap-1 rounded"
                        title={lang === "bg" ? "Копирай линк" : "Copy link"}
                        data-testid={`invite-copy-url-${inv.id}`}
                      >
                        <LinkIcon className="w-3 h-3" /> URL
                      </button>
                      <button
                        onClick={() => copyToClipboard(inv.code, lang === "bg" ? "Кодът е копиран" : "Code copied")}
                        className="px-2.5 py-1.5 border border-white/15 hover:border-pitch hover:text-pitch text-xs font-mono inline-flex items-center gap-1 rounded"
                        title={lang === "bg" ? "Копирай код" : "Copy code"}
                        data-testid={`invite-copy-code-${inv.id}`}
                      >
                        <Copy className="w-3 h-3" /> {lang === "bg" ? "Код" : "Code"}
                      </button>
                    </div>
                    {/* Share via channels — Iter 32 */}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      <span className="text-[10px] font-mono uppercase tracking-widest text-white/35">{lang === "bg" ? "Сподели" : "Share"}:</span>
                      <a
                        href={whatsappUrl} target="_blank" rel="noreferrer"
                        className="px-2.5 py-1.5 border border-blue-500/30 text-blue-300 hover:border-blue-500 hover:bg-blue-500/5 text-xs font-mono inline-flex items-center gap-1.5 rounded transition-all"
                        data-testid={`invite-share-whatsapp-${inv.id}`}
                      >
                        WhatsApp
                      </a>
                      <a
                        href={messengerUrl} target="_blank" rel="noreferrer"
                        className="px-2.5 py-1.5 border border-blue-500/30 text-blue-300 hover:border-blue-500 hover:bg-blue-500/5 text-xs font-mono inline-flex items-center gap-1.5 rounded transition-all"
                        data-testid={`invite-share-messenger-${inv.id}`}
                      >
                        Messenger
                      </a>
                      <a
                        href={emailUrl}
                        className="px-2.5 py-1.5 border border-white/15 text-white/65 hover:border-pitch hover:text-pitch text-xs font-mono inline-flex items-center gap-1.5 rounded transition-all"
                        data-testid={`invite-share-email-${inv.id}`}
                      >
                        Email
                      </a>
                    </div>
                    {inv.used_by && inv.used_by.length > 0 && (
                      <div className="mt-3 pt-2 border-t border-white/8">
                        <div className="label-mono mb-1">// {lang === "bg" ? "Регистрирали се с тази покана" : "Redeemed by"}</div>
                        <div className="flex flex-wrap gap-1.5">
                          {inv.used_by.slice(0, 8).map((u) => (
                            <span key={u.user_id} className="chip-badge text-[9px]">{u.email}</span>
                          ))}
                          {inv.used_by.length > 8 && (
                            <span className="text-[10px] text-white/40 font-mono">+{inv.used_by.length - 8} more</span>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => toggleActive(inv)}
                      className="p-2 text-white/55 hover:text-pitch hover:bg-pitch/5 rounded transition-colors"
                      title={inv.active ? (lang === "bg" ? "Спри" : "Disable") : (lang === "bg" ? "Активирай" : "Enable")}
                      data-testid={`invite-toggle-${inv.id}`}
                    >
                      {inv.active ? <Ban className="w-4 h-4" /> : <Check className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => remove(inv.id)}
                      className="p-2 text-white/40 hover:text-red-400 hover:bg-red-500/5 rounded transition-colors"
                      title={lang === "bg" ? "Изтрий" : "Delete"}
                      data-testid={`invite-delete-${inv.id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showCreate && <CreateInviteModal lang={lang} onClose={() => setShowCreate(false)} onCreated={(inv) => { setList((l) => [inv, ...l]); setShowCreate(false); }} />}
    </div>
  );
}

function CreateInviteModal({ lang, onClose, onCreated }) {
  const [form, setForm] = useState({
    note: "",
    role_hint: "",
    duration_mode: "limited", // "limited" | "lifetime"
    pro_duration_days: 90,
    max_uses_mode: "single", // "single" | "limited" | "unlimited"
    max_uses: 1,
    auto_approve: true,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = {
        note: form.note.trim() || null,
        role_hint: form.role_hint || null,
        pro_duration_days: form.duration_mode === "lifetime" ? null : Number(form.pro_duration_days),
        max_uses: form.max_uses_mode === "unlimited" ? null : (form.max_uses_mode === "single" ? 1 : Number(form.max_uses)),
        auto_approve: !!form.auto_approve,
      };
      const { data } = await api.post("/admin/invites", payload);
      toast.success(lang === "bg" ? "Поканата е създадена" : "Invite created");
      onCreated?.(data);
    } catch (e2) {
      setErr(e2?.response?.data?.detail || "Error");
    }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-end md:items-center justify-center p-0 md:p-6 animate-fade-up" onClick={onClose} data-testid="create-invite-modal">
      <div className="bg-[#0A1628] border border-white/10 w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-t-lg md:rounded-lg" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between p-5 border-b border-white/10 sticky top-0 bg-[#0A1628] z-10">
          <h2 className="font-oswald uppercase text-xl tracking-tight">
            {lang === "bg" ? "Нова покана" : "New invite"}
          </h2>
          <button onClick={onClose} className="p-1.5 hover:bg-white/5 rounded-md transition-colors"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-5 space-y-4">
          <div>
            <label className="label-mono block mb-1.5">{lang === "bg" ? "Бележка (вътрешна)" : "Note (internal)"}</label>
            <input
              type="text"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
              placeholder={lang === "bg" ? "напр. Influencer launch" : "e.g. Influencer launch"}
              className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch rounded text-sm"
              data-testid="invite-note"
              maxLength={200}
            />
          </div>
          <div>
            <label className="label-mono block mb-1.5">{lang === "bg" ? "Тип потребител (по избор)" : "Target role (optional)"}</label>
            <select
              value={form.role_hint}
              onChange={(e) => setForm({ ...form, role_hint: e.target.value })}
              className="w-full bg-[#111D33] border border-white/10 px-3 py-2.5 outline-none focus:border-pitch rounded text-sm"
              data-testid="invite-role-hint"
            >
              <option value="">{lang === "bg" ? "Всеки тип" : "Any role"}</option>
              <option value="coach">{lang === "bg" ? "Треньор" : "Coach"}</option>
              <option value="academy">{lang === "bg" ? "Академия" : "Academy"}</option>
              <option value="parent">{lang === "bg" ? "Родител" : "Parent"}</option>
              <option value="player">{lang === "bg" ? "Играч" : "Player"}</option>
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1.5">{lang === "bg" ? "Продължителност на Pro" : "Pro duration"}</label>
            <div className="grid grid-cols-2 gap-2">
              <button type="button" onClick={() => setForm({ ...form, duration_mode: "limited" })} className={`p-3 border text-xs font-mono ${form.duration_mode === "limited" ? "border-pitch bg-pitch/5 text-pitch" : "border-white/10 text-white/55"}`} data-testid="invite-duration-limited">
                {lang === "bg" ? "Ограничен" : "Limited"}
              </button>
              <button type="button" onClick={() => setForm({ ...form, duration_mode: "lifetime" })} className={`p-3 border text-xs font-mono inline-flex items-center justify-center gap-1.5 ${form.duration_mode === "lifetime" ? "border-pitch bg-pitch/5 text-pitch" : "border-white/10 text-white/55"}`} data-testid="invite-duration-lifetime">
                <InfinityIcon className="w-3.5 h-3.5" /> {lang === "bg" ? "Доживотен" : "Lifetime"}
              </button>
            </div>
            {form.duration_mode === "limited" && (
              <div className="mt-3">
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={3650}
                    value={form.pro_duration_days}
                    onChange={(e) => setForm({ ...form, pro_duration_days: e.target.value })}
                    className="w-28 bg-[#111D33] border border-white/10 px-3 py-2 outline-none focus:border-pitch rounded text-sm font-mono"
                    data-testid="invite-duration-days"
                  />
                  <span className="text-xs text-white/55 font-mono">{lang === "bg" ? "дни" : "days"}</span>
                </div>
                <div className="flex gap-1.5 mt-2">
                  {[30, 90, 180, 365].map((d) => (
                    <button key={d} type="button" onClick={() => setForm({ ...form, pro_duration_days: d })} className="px-2 py-1 text-[10px] font-mono border border-white/10 hover:border-pitch hover:text-pitch transition-colors">
                      {d}d
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <div>
            <label className="label-mono block mb-1.5">{lang === "bg" ? "Брой употреби" : "Usage limit"}</label>
            <div className="grid grid-cols-3 gap-2">
              <button type="button" onClick={() => setForm({ ...form, max_uses_mode: "single" })} className={`p-2.5 border text-[11px] font-mono ${form.max_uses_mode === "single" ? "border-pitch bg-pitch/5 text-pitch" : "border-white/10 text-white/55"}`} data-testid="invite-uses-single">
                {lang === "bg" ? "1 човек" : "Single use"}
              </button>
              <button type="button" onClick={() => setForm({ ...form, max_uses_mode: "limited" })} className={`p-2.5 border text-[11px] font-mono ${form.max_uses_mode === "limited" ? "border-pitch bg-pitch/5 text-pitch" : "border-white/10 text-white/55"}`} data-testid="invite-uses-limited">
                {lang === "bg" ? "Ограничен" : "Limited"}
              </button>
              <button type="button" onClick={() => setForm({ ...form, max_uses_mode: "unlimited" })} className={`p-2.5 border text-[11px] font-mono inline-flex items-center justify-center gap-1 ${form.max_uses_mode === "unlimited" ? "border-pitch bg-pitch/5 text-pitch" : "border-white/10 text-white/55"}`} data-testid="invite-uses-unlimited">
                <InfinityIcon className="w-3 h-3" /> {lang === "bg" ? "Неограничен" : "Unlimited"}
              </button>
            </div>
            {form.max_uses_mode === "limited" && (
              <div className="mt-3 flex items-center gap-2">
                <input
                  type="number"
                  min={1}
                  max={10000}
                  value={form.max_uses}
                  onChange={(e) => setForm({ ...form, max_uses: e.target.value })}
                  className="w-28 bg-[#111D33] border border-white/10 px-3 py-2 outline-none focus:border-pitch rounded text-sm font-mono"
                  data-testid="invite-uses-count"
                />
                <span className="text-xs text-white/55 font-mono">{lang === "bg" ? "макс. употреби" : "max redemptions"}</span>
              </div>
            )}
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={form.auto_approve}
              onChange={(e) => setForm({ ...form, auto_approve: e.target.checked })}
              className="w-4 h-4 accent-pitch"
              data-testid="invite-auto-approve"
            />
            <span className="text-xs text-white/65 font-mono">
              {lang === "bg" ? "Автоматично одобрение на треньори/академии" : "Auto-approve coaches & academies"}
            </span>
          </label>
          {err && <div className="text-red-400 text-xs font-mono" data-testid="invite-create-error">{err}</div>}
          <div className="flex gap-3 pt-3 sticky bottom-0 bg-[#0A1628] border-t border-white/10">
            <button type="button" onClick={onClose} className="flex-1 btn-secondary py-3 text-xs">{lang === "bg" ? "Отказ" : "Cancel"}</button>
            <button type="submit" disabled={busy} className="flex-1 btn-primary py-3 text-xs inline-flex items-center justify-center gap-2 disabled:opacity-50" data-testid="invite-submit">
              {busy ? <Sparkles className="w-4 h-4 animate-pulse" /> : <Sparkles className="w-4 h-4" />}
              {lang === "bg" ? "Създай" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ---------- Invite Center analytics row ---------- */
function InviteAnalyticsRow({ list, lang, filter, setFilter }) {
  const total = list.length;
  const active = list.filter((i) => i.active && !(i.max_uses && (i.used_count || 0) >= i.max_uses)).length;
  const exhausted = list.filter((i) => i.active && i.max_uses && (i.used_count || 0) >= i.max_uses).length;
  const disabled = list.filter((i) => !i.active).length;
  const redeemed = list.reduce((s, i) => s + (i.used_count || 0), 0);
  // Conversion = total redemptions / total max-use capacity. Lifetime codes
  // (no max_uses) are ignored from the denominator so a single mega-code
  // doesn't drown the metric.
  const cap = list.reduce((s, i) => s + (i.max_uses || 0), 0);
  const conv = cap > 0 ? Math.round((redeemed / cap) * 100) : null;
  const tiles = [
    { id: "all", label: lang === "bg" ? "Общо" : "Total", value: total },
    { id: "active", label: lang === "bg" ? "Активни" : "Active", value: active, accent: true },
    { id: "exhausted", label: lang === "bg" ? "Изчерпани" : "Exhausted", value: exhausted },
    { id: "disabled", label: lang === "bg" ? "Спрени" : "Disabled", value: disabled },
    { id: "used", label: lang === "bg" ? "Записани" : "Redemptions", value: redeemed, accent: redeemed > 0 },
  ];
  return (
    <div className="surface-elevated p-4 rounded" data-testid="invite-analytics-row">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div className="label-mono">// {lang === "bg" ? "Анализ на покани" : "Invite analytics"}</div>
        {conv != null && (
          <div className="text-[11px] font-mono text-white/55">
            {lang === "bg" ? "Конверсия: " : "Conversion: "}<span className="text-pitch">{conv}%</span>
            <span className="text-white/30 ml-1">({redeemed}/{cap})</span>
          </div>
        )}
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
        {tiles.map((t) => (
          <button
            key={t.id}
            onClick={() => setFilter(t.id)}
            className={`text-left p-3 border rounded transition-all ${filter === t.id ? "border-pitch bg-pitch/5" : "border-white/10 hover:border-white/30"}`}
            data-testid={`invite-filter-${t.id}`}
          >
            <div className="text-[10px] font-mono uppercase tracking-widest text-white/45">{t.label}</div>
            <div className={`font-oswald text-2xl mt-0.5 ${t.accent ? "text-pitch" : ""}`}>{t.value}</div>
          </button>
        ))}
      </div>
    </div>
  );
}

